import React, {useEffect, useMemo, useState} from "react";
import {doc, updateDoc, runTransaction} from "firebase/firestore";
import db from "../../../firebase";
import styles from "./RoomSettingsModal.module.css";
import clsx from "clsx";
import IconParticipants from '../../../assets/Icon_participants.svg';
import {useTranslation} from 'react-i18next';
import IconOwner from '../../../assets/icon-owner.svg';
import IconFacilitator from '../../../assets/icon-facilitator.svg';
import IconTrash from '../../../assets/icon-trash.svg';
import {fallbackScales, mapScaleToKey} from "../../../utils/scales";
import {removeParticipant, assignFacilitator} from "roomActions";


interface Props {
    open: boolean;
    onClose: () => void;
    roomName: string;
    onRoomNameChange: (name: string) => void;
    estimationType: string;
    onEstimationTypeChange: (type: string) => void;
    showApplyButton?: boolean;
    roomId?: string;
    mode: "withFacilitator" | "withoutFacilitator";
    onModeChange?: (mode: "withFacilitator" | "withoutFacilitator") => void;
    participants: Participant[];
    userId: string;
    ownerId: string;
    roomType: "poker" | "bucket"
}

interface Participant {
    id: string;
    name: string;
    role: string;
}


const RoomSettingsModal: React.FC<Props> = ({
                                                open,
                                                onClose,
                                                roomName,
                                                onRoomNameChange,
                                                estimationType,
                                                onEstimationTypeChange,
                                                showApplyButton = true,
                                                roomId,
                                                mode,
                                                onModeChange,
                                                participants,
                                                userId,
                                                ownerId,
                                                roomType
                                            }) => {
    const sortedParticipants = useMemo(() => {
        return [...participants].sort((a, b) => {
            if (a.id === userId) return -1;
            if (b.id === userId) return 1;

            if (a.id === ownerId && b.id !== ownerId) return -1;
            if (a.id !== ownerId && b.id === ownerId) return 1;

            if (a.role === 'facilitator' && b.role !== 'facilitator') return -1;
            if (a.role !== 'facilitator' && b.role === 'facilitator') return 1;

            return a.name.localeCompare(b.name);
        })
    }, [participants]);

    const {t, i18n} = useTranslation();
    const [scaleKeys, setScaleKeys] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        setScaleKeys(
            fallbackScales.map((s: string) => ({
                value: mapScaleToKey(s),
                label: t(`scale.${mapScaleToKey(s)}`)
            }))
        );
    }, [i18n.language]);


    const [name, setName] = useState(roomName);
    const [type, setType] = useState(estimationType);
    const [customValues, setCustomValues] = useState<string[]>([]);
    const [newCustomValue, setNewCustomValue] = useState("");
    const [localMode, setLocalMode] = useState<"withFacilitator" | "withoutFacilitator">(mode);

    const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
    const [hovered, setHovered] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<Participant>();
    const [userName, setUserName] = useState("");
    useEffect(() => {
        if (open) {
            const current = participants.find((p) => p.id === userId);
            if (current) {
                setUserName(current.name);
                setCurrentUser(current);
            }
        }
    }, [open, participants, userId]);


    const handleSetOwner = async (participantId: string) => {
        if (!roomId) return;
        const ref = doc(db, "rooms", roomId);
        await runTransaction(db, async (transaction) => {
            const roomSnap = await transaction.get(ref);
            if (!roomSnap.exists()) return;

            transaction.update(ref, {
                owner: participantId
            });
        });
    };

    const handleSetFacilitator = async (participantId: string) => {
        if (!roomId) return;
        try {
            await assignFacilitator(roomId, participantId);
        } catch (error) {
            console.error('Error updating participant role:', error);
        }
    };

    const handleRemove = async (participantId: string) => {
        if (!roomId) return;
        try {
            await removeParticipant(roomId, participantId);
        } catch (error) {
            console.error('Error removing participant:', error);
        }
    };


    useEffect(() => {
        setName(roomName);
        setType(estimationType);
        setLocalMode(mode);
    }, [roomName, estimationType, mode]);


    const handleSave = async () => {
        if (!roomId) return;

        const ref = doc(db, "rooms", roomId);

        await runTransaction(db, async (transaction) => {
            const roomSnap = await transaction.get(ref);
            if (!roomSnap.exists()) return;

            const roomData = roomSnap.data() as any;
            const prevMode = roomData.mode;
            const participants: Record<string, { name: string; role?: string }> =
                roomData.participants || {};

            const updates: Record<string, any> = {
                roomName: name,
                estimationType: type,
                mode: localMode,
                customValues: type === "custom" ? customValues : [],
                [`participants.${userId}.name`]: userName,
            };

            // Если включили режим без фасилитатора — всем участникам ставим role: "participant"
            if (prevMode !== "withoutFacilitator" && localMode === "withoutFacilitator") {
                for (const pid of Object.keys(participants)) {
                    updates[`participants.${pid}.role`] = "participant";
                }
            }

            transaction.update(ref, updates);
        });

        onRoomNameChange(name);
        onEstimationTypeChange(type);
        onModeChange?.(localMode);
        onClose();
    };

    const handleAddCustomValue = () => {
        if (newCustomValue.trim()) {
            setCustomValues((prev) => [...prev, newCustomValue.trim()]);
            setNewCustomValue("");
        }
    };

    const handleRemoveCustomValue = (index: number) => {
        setCustomValues((prev) => prev.filter((_, i) => i !== index));
    };

    if (!open) return null;

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.closeBtn} onClick={onClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" fill="#1D1B20"/>
                    </svg>
                </button>
                <h2 className={styles.modalTitle}>{t("roomSettings")}</h2>

                <label className={styles.label}>{t("roomName")}</label>
                <input className={styles.input}
                       value={name}
                       onChange={(e) => setName(e.target.value)}
                       disabled={currentUser?.role !== "facilitator" && userId != ownerId}
                />

                <label className={styles.label}>{t("yourName")}</label>
                <input
                    className={styles.input}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                />


                <label className={styles.label}>{t("estimationType")}</label>
                <select className={styles.select}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        disabled={currentUser?.role !== "facilitator" && userId != ownerId}
                >
                    {scaleKeys.map((opt: { value: string; label: string }) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>

                {type === "custom" && (currentUser?.role === "facilitator" || userId == ownerId) && (
                    <>
                        <div className={styles.customSection}>
                            {customValues.map((val, idx) => (
                                <div key={idx} className={styles.customValue}>
                                    <span>{val}</span>
                                    <button className={styles.removeButton}
                                            onClick={() => handleRemoveCustomValue(idx)}>
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className={styles.customInputRow}>
                            <input className={styles.input} value={newCustomValue}
                                   onChange={(e) => setNewCustomValue(e.target.value)}/>
                            <button className={styles.secondaryButton} onClick={handleAddCustomValue}>
                                {t("addCustomValue")}
                            </button>
                        </div>
                    </>
                )}

                {roomType === "poker" && (
                    <>
                        <label className={styles.label}>{t("manageRoom")}</label>
                        <div className={styles.radioGroup}>
                            <label className={styles.radioOption}>
                                <input
                                    type="checkbox"
                                    checked={localMode === "withFacilitator"}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setLocalMode("withFacilitator");
                                        } else {
                                            setLocalMode("withoutFacilitator");
                                        }
                                    }}
                                    disabled={currentUser?.role !== "facilitator" && userId != ownerId}
                                />
                                {t("withFacilitator")}
                            </label>
                        </div>
                    </>
                )}

                <label className={styles.label}>{t("manageParticipants")}</label>
                <div
                    className={clsx(
                        styles.selectlist,
                        isParticipantsOpen ? styles.selectListOpenActive : '',
                        isParticipantsOpen ? styles.selectArrowUp : styles.selectArrowDown
                    )}
                    onClick={() => setIsParticipantsOpen((prev) => !prev)}
                >
                    <div className={styles.participantsLine}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M15 15.75V14.25C15 13.4543 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4543 3 14.25V15.75M12 5.25C12 6.90685 10.6569 8.25 9 8.25C7.34315 8.25 6 6.90685 6 5.25C6 3.59315 7.34315 2.25 9 2.25C10.6569 2.25 12 3.59315 12 5.25Z"
                                stroke="#9865DA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        {t("participantsCount", {count: participants.length})}
                    </div>
                </div>
                {isParticipantsOpen && (
                    <div className={styles.selectlistOpen}>
                        {sortedParticipants.map((p) => (
                            <div
                                key={p.id}
                                className={styles.participantItem}
                                onMouseEnter={() => setHovered(p.id)}
                                onMouseLeave={() => setHovered(null)}
                            >
                                <div className={styles.participantNameWrapper}>
                                    <span>{p.name}</span>
                                    {p.id === userId && (
                                        <span className={`${styles.badge} ${styles.badgeYou}`}>{t("yourLabel")}</span>
                                    )}
                                    {p.role === "facilitator" && (
                                        <span
                                            className={`${styles.badge} ${styles.badgeFacilitator}`}>{t("facilitatorShort")}</span>
                                    )}
                                    {p.id === ownerId && (
                                        <span
                                            className={`${styles.badge} ${styles.badgeOwner}`}>{t("ownerShort")}</span>
                                    )}
                                </div>

                                {hovered === p.id && (
                                    (mode === "withFacilitator" && userId === ownerId) ||
                                    (mode === "withoutFacilitator" && userId === ownerId)
                                ) && (

                                    <div className={styles.actionButtons}>
                                        {p.id !== userId && (
                                            <button className={styles.iconButton} onClick={() => handleSetOwner(p.id)}>
                                                <p className={styles.tooltip}>
                                                    {t("setOwner")}
                                                </p>
                                                <img src={IconOwner} alt="Set Owner"/>
                                            </button>
                                        )}

                                        {mode === "withFacilitator" && p.role !== "facilitator" && (
                                            <button className={styles.iconButton}
                                                    onClick={() => handleSetFacilitator(p.id)}>
                                                <p className={styles.tooltip}>
                                                    {t("setFacilitator")}
                                                </p>
                                                <img src={IconFacilitator} alt="Set Facilitator"/>
                                            </button>
                                        )}

                                        {p.id !== userId && (
                                            <button className={styles.iconButton} onClick={() => handleRemove(p.id)}>
                                                <p className={styles.tooltip}>
                                                    {t("RemoveUser")}
                                                </p>
                                                <img src={IconTrash} alt="Remove"/>
                                            </button>
                                        )}
                                    </div>
                                )}

                            </div>
                        ))}
                    </div>
                )}


                {showApplyButton && (
                    <button className={styles.primaryButton} onClick={handleSave}>
                        {t("saveChanges")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default RoomSettingsModal;
